import React, { useState } from "react";
import NumberFormat from "react-number-format";
import { FormCheckbox } from "../../../../forms/FormCheckbox/FormCheckbox";
import { FormRowRaw } from "../../../../forms/FormRowRaw";
import { BaseFilterProps } from "../../lib";
import styles from "./DueDateFilter.module.scss";

interface DueDateOption {
    id: string;
    label: string;
    value: "any" | "overdue" | "not_due";
    filterValue: boolean | undefined;
}

const dueDateOptions: DueDateOption[] = [
    {
        id: "due-date-filter__option__any",
        label: "Any",
        value: "any",
        filterValue: undefined,
    },
    {
        id: "due-date-filter__option__overdue",
        label: "Overdue",
        value: "overdue",
        filterValue: true,
    },
    {
        id: "due-date-filter__option__not_due",
        label: "Not due yet",
        value: "not_due",
        filterValue: false,
    },
];

export const DueDateFilter: React.FC<BaseFilterProps> = ({
    filters,
    onChange,
}) => {
    const [overdueDays, setOverdueDays] = useState<{
        min?: number;
        max?: number;
    }>({
        min: filters.minOverdue,
        max: filters.maxOverdue,
    });

    const handleOptionChange = (option: DueDateOption) => {
        if (option.value === "any") {
            onChange({
                overdue: undefined,
                minOverdue: undefined,
                maxOverdue: undefined,
            });
        } else if (option.value === "not_due") {
            onChange({
                overdue: false,
                minOverdue: undefined,
                maxOverdue: undefined,
            });
        } else {
            // For overdue, we'll just set the flag and let the days inputs handle the range
            onChange({
                overdue: true,
                minOverdue: undefined,
                maxOverdue: undefined,
            });
        }
    };

    const handleOverdueDaysChange = (
        type: "min" | "max",
        value: number | undefined,
    ) => {
        setOverdueDays((prev) => ({ ...prev, [type]: value }));

        onChange({
            overdue: true,
            minOverdue: type === "min" ? value : overdueDays.min,
            maxOverdue: type === "max" ? value : overdueDays.max,
        });
    };

    // Determine which option is currently selected
    const getSelectedOption = () => {
        if (filters.overdue === true) {
            return "overdue";
        } else if (filters.overdue === false) {
            return "not_due";
        } else {
            return "any";
        }
    };

    const isOverdueSelected = getSelectedOption() === "overdue";

    return (
        <div className={styles.dueDateFilter}>
            <section className={styles.options}>
                <header className={styles.header}>Due Date</header>
                {dueDateOptions.map((option) => (
                    <FormCheckbox
                        key={option.id}
                        value={option.value}
                        type="radio"
                        name="dueDate"
                        label={option.label}
                        isChecked={getSelectedOption() === option.value}
                        handleChange={() => handleOptionChange(option)}
                        small
                    />
                ))}
            </section>

            {isOverdueSelected && (
                <section>
                    <div className={styles.values}>
                        <FormRowRaw
                            value={overdueDays.min ?? ""}
                            label="Min days"
                            size="sm"
                            field={
                                <NumberFormat
                                    value={overdueDays.min ?? undefined}
                                    onValueChange={(v) =>
                                        handleOverdueDaysChange(
                                            "min",
                                            v.floatValue,
                                        )
                                    }
                                    allowEmptyFormatting
                                    className="form-control form-control-sm"
                                    allowNegative={false}
                                />
                            }
                        />

                        <FormRowRaw
                            value={overdueDays.max ?? ""}
                            label="Max days"
                            size="sm"
                            field={
                                <NumberFormat
                                    value={overdueDays.max ?? undefined}
                                    onValueChange={(v) =>
                                        handleOverdueDaysChange(
                                            "max",
                                            v.floatValue,
                                        )
                                    }
                                    allowEmptyFormatting
                                    className="form-control form-control-sm"
                                    allowNegative={false}
                                />
                            }
                        />
                    </div>
                </section>
            )}
        </div>
    );
};
